import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { parseHtmlWithScriptRemoval } from "@MasterBluePrint/Overrides/Common/Utilities/parseHtmlContent";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    warningIcon = "warningIcon",
    warningLabel = "warningLabel",
    warningMessage = "warningMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.warningIcon]: string;
        [fields.warningLabel]: string;
        [fields.warningMessage]: string;
    };
}

type Props = OwnProps & WidgetProps & HasProduct;

export interface ProductDetailsProp65MessageStyles {
    wrapper?: InjectableCss;
    warningIcon?: InjectableCss;
    textWrapper?: InjectableCss;
    warningText?: TypographyProps;
    descriptionText?: TypographyProps;
}

const prop65MessageStyles: ProductDetailsProp65MessageStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: center;
            align-items: flex-start;
            padding: 1rem 1.2rem;
        `,
    },
    warningIcon: {
        css: css`
            width: 30px;
            height: 27px;
            flex-shrink: 0;
            margin-top: 0.3rem;
        `,
    },
    textWrapper: {
        css: css`
            width: 100%;
            flex-shrink: 0;
        `,
    },
    warningText: {
        css: css`
            color: #002553;
            leading-trim: both;
            text-edge: cap;
            font-family: Roboto, sans-serif;
            font-size: 13px;
            font-style: normal;
            font-weight: 700;
            line-height: 21.3px;

            @media (max-width: 767px) {
                line-height: 18.1px;
            }
        `,
    },
    descriptionText: {
        css: css`
            color: #002553;
            leading-trim: both;
            text-edge: cap;
            font-family: Roboto, sans-serif;
            font-size: 13px;
            font-style: normal;
            font-weight: 400;
            line-height: 21.3px;
            margin-left: 0.2rem;

            @media (max-width: 767px) {
                line-height: 18.1px;
            }
        `,
    },
};

const styles = prop65MessageStyles;

function linkifyText(text: string): string {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/i;
    return text.replace(urlRegex, match => {
        const url = match.startsWith("http") ? match : `https://${match}`;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer"  style="text-decoration: underline;">${match}</a>`;
    });
}

const ProductDetailsProp65Message: FC<Props> = ({ fields, product }) => {
    const featureFlags: string[] = ["TEUS-8731"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const displayProp65Warning = flagValues["TEUS-8731"];
    const showProp65Warning =
        displayProp65Warning &&
        product?.properties?.californiaProp65?.trim().toLowerCase() === "y";

    if (!fields.warningMessage || !fields.warningIcon) {
        return null;
    }

    const linkedWarningMessage = linkifyText(fields.warningMessage);

    return (
        <>
            {showProp65Warning &&
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.warningIcon}>
                        {parseHtmlWithScriptRemoval(fields.warningIcon)}
                    </StyledWrapper>
                    <StyledWrapper {...styles.textWrapper}>
                        <Typography {...styles.warningText}>
                            {fields.warningLabel}
                        </Typography>
                        <Typography {...styles.descriptionText}>
                            {parseHtmlWithScriptRemoval(linkedWarningMessage)}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            }
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(withProduct(ProductDetailsProp65Message)),
    definition: {
        group: "Product Details",
        icon: "Rtf",
        displayName: "Prop65 Warning Message",
        fieldDefinitions: [
            {
                name: fields.warningIcon,
                displayName: "Warning Icon",
                editorTemplate: "MultilineTextField",
                defaultValue: '<svg id="a" data-name="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 29" width="21" height="22">   <polygon points="16 1 1 28 31 28 16 1" style="fill: #eed202; stroke: #000; stroke-linejoin: round; stroke-width: 2px;"/>   <path d="M15.58,20.2c-.98-5.96-1.48-9.17-1.48-9.65,0-.57.19-1.04.57-1.39.38-.35.81-.53,1.28-.53.52,0,.95.19,1.31.57s.53.82.53,1.33-.49,3.71-1.48,9.67h-.74ZM17.58,23.05c0,.46-.16.84-.48,1.17s-.7.48-1.15.48-.84-.16-1.16-.48-.48-.71-.48-1.17.16-.83.48-1.16.71-.48,1.16-.48.83.16,1.15.48.48.71.48,1.16Z"/> </svg>',
                fieldType: "General",
            },
            {
                name: fields.warningLabel,
                displayName: "Warning Label",
                editorTemplate: "TextField",
                defaultValue: "WARNING:",
                fieldType: "General",
            },
            {
                name: fields.warningMessage,
                displayName: "Warning Message",
                editorTemplate: "TextField",
                defaultValue: "Product can expose you to chemicals which are known to State of California to cause cancer and/or birth defects or other reproductive harm. For more information go to www.P65Warnings.ca.gov",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;